import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

// Import step components
import DemoUploadVideosStep from "../components/onboarding/DemoUploadVideosStep";
import ProcessingStep from "../components/onboarding/ProcessingStep";
import AppUrlLoginStep from "../components/onboarding/AppUrlLoginStep";
import DemoSuccessStep from "../components/onboarding/DemoSuccessStep";
import OnboardingProgress from "../components/onboarding/OnboardingProgress";

// Interactive demo flow configuration (without Guide, Stripe payment and Slack support)
const DEMO_STEPS = {
  UPLOAD_VIDEOS: 1,
  PROCESSING: 2,
  APP_URL_LOGIN: 3,
  SUCCESS: 4
};

const STEP_NAMES = {
  [DEMO_STEPS.UPLOAD_VIDEOS]: "Upload Videos",
  [DEMO_STEPS.PROCESSING]: "Processing",
  [DEMO_STEPS.APP_URL_LOGIN]: "App Setup",
  [DEMO_STEPS.SUCCESS]: "Complete"
};

// Demo state management (separate from onboarding to avoid conflicts)
const DemoStateManager = {
  getCurrentStep: () => {
    try {
      const state = JSON.parse(localStorage.getItem('demoState') || '{}');
      return state.currentStep || DEMO_STEPS.UPLOAD_VIDEOS;
    } catch {
      return DEMO_STEPS.UPLOAD_VIDEOS;
    }
  },
  
  setCurrentStep: (step) => {
    const state = JSON.parse(localStorage.getItem('demoState') || '{}');
    state.currentStep = step;
    state.completedSteps = state.completedSteps || [];
    if (!state.completedSteps.includes(step - 1) && step > 1) {
      // Don't allow skipping steps
      return false;
    }
    if (step === DEMO_STEPS.SUCCESS) {
      state.completedSteps = [1, 2, 3];
    } else if (!state.completedSteps.includes(step)) {
      state.completedSteps.push(step);
    }
    localStorage.setItem('demoState', JSON.stringify(state));
    return true;
  },
  
  isStepAccessible: (step) => {
    const state = JSON.parse(localStorage.getItem('demoState') || '{}');
    const completedSteps = state.completedSteps || [];
    
    // First step is always accessible
    if (step === DEMO_STEPS.UPLOAD_VIDEOS) return true;
    
    // For other steps, check if previous step is completed
    return completedSteps.includes(step - 1);
  },
  
  markStepCompleted: (step) => {
    const state = JSON.parse(localStorage.getItem('demoState') || '{}');
    state.completedSteps = state.completedSteps || [];
    if (!state.completedSteps.includes(step)) {
      state.completedSteps.push(step);
    }
    localStorage.setItem('demoState', JSON.stringify(state));
  },
  
  reset: () => {
    localStorage.removeItem('demoState');
  },
  
  isComplete: () => {
    const state = JSON.parse(localStorage.getItem('demoState') || '{}');
    return state.completedSteps && state.completedSteps.includes(DEMO_STEPS.SUCCESS);
  }
};

export default function InteractiveDemo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(DEMO_STEPS.UPLOAD_VIDEOS);
  const [uploadedVideos, setUploadedVideos] = useState([]);

  // Demo step management (no authentication required)
  useEffect(() => {
    const savedStep = DemoStateManager.getCurrentStep();
    
    // Validate step access
    if (!DemoStateManager.isStepAccessible(savedStep)) {
      // Redirect to the appropriate step
      const validStep = DemoStateManager.getCurrentStep();
      setCurrentStep(validStep);
      
      // Update URL to match the valid step
      if (validStep === DEMO_STEPS.UPLOAD_VIDEOS) {
        navigate('/demo', { replace: true });
      }
      return;
    }
    
    // Update step if valid
    if (DemoStateManager.setCurrentStep(savedStep)) {
      setCurrentStep(savedStep);
    }
  }, [location, navigate]);

  useEffect(() => {
    // Set demo flag when component mounts
    localStorage.setItem('inDemo', 'true');
    
    // Check if demo is already complete
    if (DemoStateManager.isComplete()) {
      localStorage.setItem('inDemo', 'false');
      navigate('/');
      return;
    }
  }, [navigate, currentStep]);

  const advanceToNextStep = (data = null) => {
    // Store uploaded videos when coming from upload step
    if (currentStep === DEMO_STEPS.UPLOAD_VIDEOS && data) {
      setUploadedVideos(data);
    }

    const nextStep = currentStep + 1;
    DemoStateManager.markStepCompleted(currentStep);
    if (DemoStateManager.setCurrentStep(nextStep)) {
      setCurrentStep(nextStep);
    }
  };

  const goBackToPreviousStep = () => {
    const previousStep = currentStep - 1;
    if (previousStep >= DEMO_STEPS.UPLOAD_VIDEOS) {
      // Don't mark the current step as completed when going back
      if (DemoStateManager.setCurrentStep(previousStep)) {
        setCurrentStep(previousStep);
      }
    }
  };

  const handleComplete = () => {
    // Mark demo as complete
    DemoStateManager.markStepCompleted(DEMO_STEPS.SUCCESS);
    
    // Remove demo flags when demo is complete
    localStorage.setItem('inDemo', 'false');
    
    // Clean up demo state
    DemoStateManager.reset();
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case DEMO_STEPS.UPLOAD_VIDEOS:
        return <DemoUploadVideosStep onComplete={advanceToNextStep} />;
      
      case DEMO_STEPS.PROCESSING:
        return <ProcessingStep onComplete={advanceToNextStep} onGoBack={goBackToPreviousStep} uploadedVideos={uploadedVideos} />;
      
      case DEMO_STEPS.APP_URL_LOGIN:
        return <AppUrlLoginStep onComplete={advanceToNextStep} />;
      
      case DEMO_STEPS.SUCCESS:
        return <DemoSuccessStep onComplete={handleComplete} />;
      
      default:
        return <DemoUploadVideosStep onComplete={advanceToNextStep} />;
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#121212",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {renderCurrentStep()}
      
      {/* Progress tracker is hidden for clean Apple-style design */}
      <OnboardingProgress currentStep={currentStep} visible={false} />
    </Box>
  );
} 